import type { ClientAccess } from '@/features/auth/permissions';
import { api } from '@/lib/api';

/** What the API tells the frontend about the signed-in person (login and /api/auth/me). */
export interface UserProfile {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    roleCode: string | null;
    /** What the role allows, by name ("ManageUsers", ...). The server enforces; this only decides what to show. */
    permissions: string[];
    /** Every client, or only the ones this person manages (ALS-95). */
    clientAccess: ClientAccess;
}

export const authApi = {
    me: () => api<UserProfile>('/api/auth/me', { expectsSession: false }),

    login: (email: string, password: string) =>
        api<UserProfile>('/api/auth/login', {
            method: 'POST',
            body: { email, password },
            expectsSession: false,
        }),

    logout: () => api('/api/auth/logout', { method: 'POST', expectsSession: false }),

    /**
     * The person is still at the screen (ALS-494): starts the server's idle clock again and
     * answers the limit. A 401 here is the session having ended, and goes to sign-in like any other.
     */
    keepAlive: () =>
        api<{ sessionTimeoutMinutes: number }>('/api/auth/keep-alive', { method: 'POST' }),

    /** Always 202: the API never says whether the address has an account. */
    requestPasswordReset: (email: string) =>
        api('/api/auth/password/reset-request', {
            method: 'POST',
            body: { email },
            expectsSession: false,
        }),

    resetPassword: (token: string, newPassword: string) =>
        api('/api/auth/password/reset', {
            method: 'POST',
            body: { token, newPassword },
            expectsSession: false,
        }),
};
